"use client";

import { withParentSize } from "@visx/responsive";
import { scaleTime } from "@visx/scale";
import { Line } from "@visx/shape";
import { Zoom } from "@visx/zoom";
import { bisector } from "d3-array";
import React, { Children, isValidElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ChartContext,
  defaultAnimationSettings,
  type TooltipData,
  type YFormatType,
} from "../../../context/ChartContext";
import { useChartExport } from "../../../hooks/useChartExport";
import { useColorMode } from "../../../utils/ColorModeProvider";
import {
  type BaseDataPoint,
  createXScale,
  createYScale,
  defaultMargin,
  getYFormatSettings,
} from "../../../utils/charts";
import { getDefaultChartColor, getThemeCategoricalColors } from "../../../utils/colors";
import { getMaxTextWidth } from "../../../utils/measureText";
import { Skeleton } from "../../Skeleton";
import { AreaSeries } from "../AreaSeries";
import { BarSeries } from "../BarSeries";
import { ChartAxis, calculateOptimalYTickCount } from "../ChartAxis";
import { ChartBottomBar } from "../ChartBottomBar";
import { ChartBrush } from "../ChartBrush";
import { ChartEventMarkers } from "../ChartEventMarkers";
import { ChartTooltip } from "../ChartTooltip";
import { LineSeries } from "../LineSeries";

type SeriesProps =
  | React.ComponentProps<typeof AreaSeries>
  | React.ComponentProps<typeof LineSeries>
  | React.ComponentProps<typeof BarSeries>;

const isSeriesElement = (child: React.ReactElement<unknown>): child is React.ReactElement<SeriesProps> =>
  child.type === AreaSeries || child.type === LineSeries || child.type === BarSeries;

const isEventMarkersElement = (
  child: React.ReactElement<unknown>
): child is React.ReactElement<React.ComponentProps<typeof ChartEventMarkers>> => child.type === ChartEventMarkers;

interface ChartContainerProps {
  children: React.ReactNode;
  parentWidth: number;
  parentHeight: number;
  /**
   * Y-axis formatter using the main formatting system
   * Accepts ComponentFormatter types (string shorthand or full options)
   * @example "energy", "currency", { type: "temperature", unit: "F" }
   */
  yFormatter?: YFormatType;
  isLoading?: boolean;
  /** Enable scroll-to-zoom and pan on main chart */
  enableZoom?: boolean;
  /** Show brush overview chart below */
  enableBrush?: boolean;
  /** Height of brush chart (default: 60px) */
  brushHeight?: number;
  /** Callback when zoom range changes */
  onZoomChange?: (domain: [Date, Date] | null) => void;
}

const bisectDate = bisector<BaseDataPoint, Date>((d) => d.xValue).left;

const ChartEmptyState: React.FC = () => (
  <div className="flex h-full items-center justify-center">
    <p className="text-gray-500">No data available</p>
  </div>
);

const ChartLoading: React.FC = () => (
  <div className="flex h-full flex-col gap-4 p-4">
    <Skeleton className="h-full w-full" />
    <div className="flex gap-4">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-4 w-24" />
    </div>
  </div>
);

const VisXChartContainer: React.FC<ChartContainerProps> = ({
  parentWidth,
  parentHeight,
  yFormatter = { type: "number" },
  isLoading = false,
  enableZoom = false,
  enableBrush = false,
  brushHeight = 60,
  onZoomChange,
  children,
}) => {
  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null);
  const [zoomDomain, setZoomDomain] = useState<[Date, Date] | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const lastMoveRef = useRef<number>(0);
  const lastTransformRef = useRef<string>("");
  // biome-ignore lint/suspicious/noExplicitAny: Zoom instance type from visx is complex and not fully exported
  const zoomRef = useRef<any>(null); // Store zoom instance to programmatically control it

  // Get color mode to trigger re-render when theme changes
  const { colorMode } = useColorMode();

  // Get theme-aware colors
  const themeColors = useMemo(() => getThemeCategoricalColors(), [colorMode]);
  const defaultColor = useMemo(() => getDefaultChartColor(), [colorMode]);

  // Extract datasets and metadata from children, and assign colors
  const {
    datasets,
    seriesMetadata,
    categoryInfo,
    categoryColors,
    categoryLabels,
    hasBarSeries,
    firstBarSeriesIndex,
    seriesTypes,
  } = useMemo(() => {
    const childArray = Children.toArray(children);
    const seriesChildren = childArray.filter(
      (child): child is React.ReactElement<SeriesProps> => isValidElement(child) && isSeriesElement(child)
    );

    const totalSeries = seriesChildren.filter((child) => child.props.data).length;
    const hasBarSeries = seriesChildren.some((child) => child.type === BarSeries);

    const data: BaseDataPoint[][] = [];
    const metadata: Array<{ label: string; color: string }> = [];
    const seriesTypes: Array<"area" | "line" | "bar"> = [];
    let categoryColors: Record<string, string> | null = null;
    let categoryLabels: Record<string, string> | null = null;
    let firstBarSeriesIndex: number | null = null;

    seriesChildren.forEach((child, seriesIndex) => {
      const childProps = child.props as SeriesProps;
      if (!childProps.data) {
        return;
      }

      data.push(childProps.data as BaseDataPoint[]);

      const color = "color" in childProps && childProps.color ? childProps.color : undefined;
      const resolvedColor = color || (totalSeries === 1 ? defaultColor : themeColors[seriesIndex % themeColors.length]);

      metadata.push({
        label: ("label" in childProps && childProps.label) || `Series ${data.length}`,
        color: resolvedColor,
      });

      const seriesType = child.type === AreaSeries ? "area" : child.type === LineSeries ? "line" : "bar";
      seriesTypes.push(seriesType);

      // Check if this child has categoryColors/categoryLabels (any series with category-based coloring)
      if ("categoryColors" in childProps && childProps.categoryColors) {
        categoryColors = childProps.categoryColors as Record<string, string>;
      }
      if ("categoryLabels" in childProps && childProps.categoryLabels) {
        categoryLabels = childProps.categoryLabels as Record<string, string>;
      }

      // Track first bar series for tooltip highlighting
      if (child.type === BarSeries && firstBarSeriesIndex === null) {
        firstBarSeriesIndex = data.length - 1;
      }
    });

    // Extract unique categories if categoryColors is being used
    let categories: Array<{ label: string; color: string }> | null = null;
    if (categoryColors) {
      const categorySet = new Set<string>();
      data.forEach((dataset) => {
        dataset.forEach((point) => {
          if (point.category) {
            categorySet.add(point.category);
          }
        });
      });

      categories = Array.from(categorySet).map((category) => ({
        label: categoryLabels?.[category] || category,
        color: categoryColors![category] || "#6b7280",
      }));
    }

    return {
      datasets: data,
      seriesMetadata: metadata,
      categoryInfo: categories,
      categoryColors: categoryColors,
      categoryLabels: categoryLabels,
      hasBarSeries: hasBarSeries,
      firstBarSeriesIndex,
      seriesTypes,
    };
  }, [children, defaultColor, themeColors, colorMode]);

  const width = parentWidth;
  // Subtract ChartBottomBar height (60px) + optional brush height
  const brushHeightAdjustment = enableBrush ? brushHeight + 8 : 0; // 8px for spacing
  const calculatedHeight = parentHeight - 60 - brushHeightAdjustment;
  // Ensure minimum height of 100px to avoid negative values
  const height = Math.max(100, calculatedHeight);

  // Combine all datasets for scale creation
  const allData = useMemo(() => datasets.flat(), [datasets]);

  // Full data domain (entire dataset)
  const fullDomain = useMemo<[Date, Date] | null>(() => {
    if (allData.length === 0) return null;
    const sortedData = [...allData].sort((a, b) => a.xValue.getTime() - b.xValue.getTime());
    return [sortedData[0].xValue, sortedData[sortedData.length - 1].xValue];
  }, [allData]);

  // Current visible domain (zoomed or full)
  const visibleDomain = zoomDomain || fullDomain;

  // Filter data to visible domain for performance
  const filteredDatasets = useMemo(() => {
    if (!visibleDomain) return datasets;

    const filtered = datasets.map((seriesData) =>
      seriesData.filter((d) => d.xValue >= visibleDomain[0] && d.xValue <= visibleDomain[1])
    );

    return filtered;
  }, [datasets, visibleDomain]);

  // Notify parent of zoom changes
  useEffect(() => {
    if (onZoomChange) {
      onZoomChange(zoomDomain);
    }
  }, [zoomDomain, onZoomChange]);

  // Use filtered data for y-scale to get accurate range for visible data
  const yScaleData = useMemo(() => filteredDatasets.flat(), [filteredDatasets]);
  const yScale = useMemo(() => createYScale(yScaleData, height, yFormatter), [yScaleData, height, yFormatter]);

  // Calculate optimal Y-axis tick count based on available height
  // Use estimated inner height (height minus default margins) for initial calculation
  const yTickCount = useMemo(() => {
    const estimatedInnerHeight = height - defaultMargin.top - defaultMargin.bottom;
    return calculateOptimalYTickCount(estimatedInnerHeight);
  }, [height]);

  // Calculate dynamic left margin based on y-axis label widths
  const dynamicMargin = useMemo(() => {
    const formatSettings = getYFormatSettings(yFormatter);
    const tickFormat = formatSettings.tickFormat || formatSettings.format;

    // Get tick values using optimal tick count
    const tickValues = yScale.ticks(yTickCount);
    const formattedTicks = tickValues.map((value) => tickFormat(value));

    // Measure the widest label
    const maxLabelWidth = getMaxTextWidth(formattedTicks, 12, "system-ui");

    // Add minimal padding: tick mark (8px) + small gap (4px)
    // Labels are right-aligned so they sit flush against the margin
    const calculatedLeftMargin = Math.ceil(maxLabelWidth) + 12;

    return {
      ...defaultMargin,
      left: Math.max(calculatedLeftMargin, 30), // Minimum 30px for very small values
    };
  }, [yScale, yFormatter, yTickCount]);

  // Create scales based on visible domain (uses dynamicMargin)
  const xScale = useMemo(() => {
    if (!visibleDomain) return createXScale(allData, width);

    return scaleTime({
      domain: visibleDomain,
      range: [dynamicMargin.left, width - dynamicMargin.right],
    });
  }, [visibleDomain, allData, width, dynamicMargin]);

  // Update zoom domain when transform changes
  const handleZoomTransform = useCallback(
    (transformMatrix: {
      scaleX: number;
      scaleY: number;
      translateX: number;
      translateY: number;
      skewX: number;
      skewY: number;
    }) => {
      if (!fullDomain) return;

      const { scaleX, translateX } = transformMatrix;

      // Calculate the visible range in pixel space
      const chartWidth = width - dynamicMargin.left - dynamicMargin.right;
      const [minX, maxX] = fullDomain;
      const totalDuration = maxX.getTime() - minX.getTime();

      // Guard against single-point or invalid domain
      if (totalDuration <= 0) {
        setZoomDomain(null);
        return;
      }

      // Apply inverse transform to get the data domain visible in the viewport
      // The transform maps data space to screen space, so we need to invert it
      const visibleStartPixel = -translateX;
      const visibleEndPixel = visibleStartPixel + chartWidth;

      // Convert pixel positions back to data domain
      const pixelsPerMs = (chartWidth * scaleX) / totalDuration;
      const startOffset = visibleStartPixel / pixelsPerMs;
      const endOffset = visibleEndPixel / pixelsPerMs;

      const newMin = new Date(minX.getTime() + startOffset);
      const newMax = new Date(minX.getTime() + endOffset);

      // Check if the new domain is essentially the same as the full domain
      // If so, set zoomDomain to null (no zoom)
      const minDiff = Math.abs(newMin.getTime() - minX.getTime());
      const maxDiff = Math.abs(newMax.getTime() - maxX.getTime());
      const threshold = totalDuration * 0.02; // 2% of total duration

      if (minDiff < threshold && maxDiff < threshold) {
        setZoomDomain(null);
      } else {
        setZoomDomain([newMin, newMax]);
      }
    },
    [fullDomain, width, dynamicMargin]
  );

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<SVGRectElement>) => {
      const { left } = svgRef.current?.getBoundingClientRect() || { left: 0 };
      const mouseX = event.clientX - left;

      // Throttle the mousemove event
      if (event.timeStamp - (lastMoveRef.current || 0) < 16) {
        return;
      }
      lastMoveRef.current = event.timeStamp;

      // Find the closest x value
      const xValue = xScale.invert(mouseX);

      // Find the nearest points from all series
      const seriesData = datasets
        .map((data, datasetIndex) => {
          if (!data || data.length === 0) return null;

          const idx = bisectDate(data, xValue, 1);
          if (idx === 0) return { point: data[0], datasetIndex };
          if (idx >= data.length) return { point: data[data.length - 1], datasetIndex };

          const d0 = data[idx - 1];
          const d1 = data[idx];
          if (!d0 || !d1) return null;

          const distanceToD0 = Math.abs(d0.xValue.getTime() - xValue.getTime());
          const distanceToD1 = Math.abs(d1.xValue.getTime() - xValue.getTime());
          const closestPoint = distanceToD0 < distanceToD1 ? d0 : d1;

          return { point: closestPoint, datasetIndex };
        })
        .filter(
          (item): item is { point: BaseDataPoint; datasetIndex: number } =>
            item !== null && seriesMetadata[item.datasetIndex] !== undefined
        )
        .map(({ point, datasetIndex }) => ({
          label: seriesMetadata[datasetIndex].label,
          value: point.yValue,
          color: seriesMetadata[datasetIndex].color,
          point,
          type: seriesTypes[datasetIndex],
        }));

      if (seriesData.length === 0) {
        setTooltipData(null);
        return;
      }

      const tooltipX = xScale(seriesData[0].point.xValue);
      const shouldShowLeft = tooltipX > (width - dynamicMargin.right - dynamicMargin.left) / 2 + dynamicMargin.left;

      setTooltipData({
        xValue: seriesData[0].point.xValue,
        series: seriesData.map(({ label, value, color, point, type }) => ({
          label,
          value,
          color,
          type,
          category: point.category,
          categoryColor: point.category && categoryColors ? categoryColors[point.category] : undefined,
        })),
        x: tooltipX,
        y: yScale(seriesData[0].value),
        position: shouldShowLeft ? "left" : "right",
      });
    },
    [datasets, filteredDatasets, seriesMetadata, seriesTypes, xScale, yScale, width, categoryColors, dynamicMargin]
  );

  const handleMouseLeave = () => {
    setTooltipData(null);
  };

  const handleResetZoom = useCallback(() => {
    setZoomDomain(null);
    // Also reset the zoom transform
    if (zoomRef.current) {
      zoomRef.current.reset();
    }
  }, []);

  const handleBrushChange = useCallback(
    (domain: [Date, Date] | null) => {
      setZoomDomain(domain);

      // Programmatically update the zoom transform to match the brush selection
      if (zoomRef.current && fullDomain) {
        const [minX, maxX] = fullDomain;
        const totalDuration = maxX.getTime() - minX.getTime();

        // Guard against single-point or invalid domain
        if (totalDuration <= 0) {
          zoomRef.current.reset();
          return;
        }

        if (!domain) {
          // Reset zoom to identity transform
          zoomRef.current.reset();
        } else {
          // Calculate the transform that corresponds to this domain
          const [newMin, newMax] = domain;
          const visibleDuration = newMax.getTime() - newMin.getTime();

          // Guard against invalid brush selection
          if (visibleDuration <= 0) {
            zoomRef.current.reset();
            return;
          }

          const scaleX = totalDuration / visibleDuration;

          // Calculate translateX
          const chartWidth = width - dynamicMargin.left - dynamicMargin.right;
          const startOffset = newMin.getTime() - minX.getTime();
          const translateX = -(startOffset / totalDuration) * chartWidth * scaleX;

          // Apply the transform
          zoomRef.current.setTransformMatrix({
            scaleX,
            scaleY: 1,
            translateX,
            translateY: 0,
            skewX: 0,
            skewY: 0,
          });
        }
      }
    },
    [fullDomain, width, dynamicMargin]
  );

  const contextValue = {
    width,
    height,
    xScale,
    yScale,
    yFormatter,
    animationSettings: defaultAnimationSettings,
    tooltip: {
      data: tooltipData,
      show: setTooltipData,
      hide: () => setTooltipData(null),
    },
    datasets: filteredDatasets,
    seriesMetadata,
    seriesTypes,
    categoryColors: categoryColors || undefined,
    categoryLabels: categoryLabels || undefined,
  };

  // Separate series children from event markers
  const childArray = Children.toArray(children);
  let seriesIndex = 0;
  const seriesChildren: React.ReactNode[] = [];
  const eventMarkerChildren: React.ReactNode[] = [];

  childArray.forEach((child) => {
    if (!isValidElement(child)) {
      seriesChildren.push(child);
      return;
    }

    if (isEventMarkersElement(child)) {
      // Collect event markers to render later (on top)
      eventMarkerChildren.push(child);
    } else if (isSeriesElement(child)) {
      // Clone series with assigned colors and filtered data
      const currentSeriesIndex = seriesIndex;
      seriesIndex++;
      const childProps = child.props as SeriesProps;
      seriesChildren.push(
        React.cloneElement(child, {
          ...childProps,
          data: filteredDatasets[currentSeriesIndex] || childProps.data,
          color: seriesMetadata[currentSeriesIndex]?.color,
        } as Partial<SeriesProps>)
      );
    } else {
      // Other children (axes, etc.)
      seriesChildren.push(child);
    }
  });

  // Get unique series for the legend
  // Use categoryInfo if available (for category-based coloring), otherwise use seriesMetadata
  const uniqueSeries = (categoryInfo || seriesMetadata).map((meta) => ({
    category: meta.label,
    fill: meta.color,
  }));

  // Handle export
  const handleExport = useChartExport({
    datasets,
    metadata: {
      xLabel: "Date",
      yLabel: "Value",
      seriesLabels: seriesMetadata.map((s) => s.label),
      timestamp: true,
    },
    svgRef,
  });

  // Return loading state if isLoading is true
  if (isLoading) {
    return <ChartLoading />;
  }

  // Check if we have any data
  if (datasets.length === 0) {
    return <ChartEmptyState />;
  }

  // SVG content to render (shared between zoom and non-zoom)
  const chartContent = (
    <>
      <title>Data chart visualization</title>
      {/* Grid lines */}
      <g>
        {yScale.ticks(yTickCount).map((tickValue) => (
          <Line
            key={`grid-line-${tickValue}`}
            from={{ x: dynamicMargin.left, y: yScale(tickValue) }}
            to={{ x: width - dynamicMargin.right, y: yScale(tickValue) }}
            stroke="var(--color-border-muted)"
            strokeWidth={1}
          />
        ))}
      </g>

      {/* Render series */}
      {seriesChildren}

      {/* Axes */}
      <ChartAxis.Bottom
        top={height - dynamicMargin.bottom}
        scale={xScale}
        width={width - dynamicMargin.left - dynamicMargin.right}
      />
      <ChartAxis.Left
        left={dynamicMargin.left}
        scale={yScale}
        yFormatter={yFormatter}
        height={height - dynamicMargin.top - dynamicMargin.bottom}
      />

      {/* Tooltip highlights */}
      {tooltipData && !tooltipData.isEvent && (
        <>
          {hasBarSeries && firstBarSeriesIndex !== null
            ? (() => {
                const barDataset = filteredDatasets[firstBarSeriesIndex] ?? [];
                if (barDataset.length === 0) {
                  return null;
                }
                const sortedData = [...barDataset].sort((a, b) => a.xValue.getTime() - b.xValue.getTime());
                const currentIndex = sortedData.findIndex((d) => d.xValue.getTime() === tooltipData.xValue.getTime());
                const nextPoint = sortedData[currentIndex + 1];
                const timeInterval =
                  sortedData.length > 1 && nextPoint
                    ? nextPoint.xValue.getTime() - tooltipData.xValue.getTime()
                    : 15 * 60000;

                const barWidth = Math.abs(
                  xScale(new Date(tooltipData.xValue.getTime() + timeInterval)) - xScale(tooltipData.xValue)
                );

                const x = xScale(tooltipData.xValue);
                const barX = x - barWidth / 2;
                const yRange = yScale.range();

                return (
                  <rect
                    x={barX}
                    y={yRange[1]}
                    width={barWidth}
                    height={yRange[0] - yRange[1]}
                    className="fill-background-muted"
                    opacity={0.5}
                    pointerEvents="none"
                    style={{ transition: "all 100ms ease-out" }}
                  />
                );
              })()
            : null}
          {tooltipData.series
            .filter((series) => series.type !== "bar")
            .map((series) => (
              <g key={`highlight-${series.label}`}>
                <circle
                  cx={xScale(tooltipData.xValue)}
                  cy={yScale(series.value)}
                  r={8}
                  fill="white"
                  stroke={series.color}
                  strokeWidth={2}
                  style={{ transition: "all 100ms ease-out" }}
                />
                <circle
                  cx={xScale(tooltipData.xValue)}
                  cy={yScale(series.value)}
                  r={4}
                  fill={series.color}
                  style={{ transition: "all 100ms ease-out" }}
                />
              </g>
            ))}
        </>
      )}

      {/* Interactive area */}
      <rect
        x={dynamicMargin.left}
        y={dynamicMargin.top}
        width={width - dynamicMargin.left - dynamicMargin.right}
        height={height - dynamicMargin.top - dynamicMargin.bottom}
        fill="transparent"
        style={{ cursor: enableZoom ? "grab" : "crosshair" }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      />

      {/* Event markers - render on top of interactive area */}
      {eventMarkerChildren}
    </>
  );

  return (
    <div className="relative flex h-full flex-col">
      <ChartContext.Provider value={contextValue}>
        {enableZoom ? (
          <Zoom
            width={width}
            height={height}
            scaleXMin={1}
            scaleXMax={20}
            scaleYMin={1}
            scaleYMax={1}
            constrain={(transformMatrix) => {
              const { scaleX, translateX } = transformMatrix;

              // Constrain scale
              const constrainedScaleX = Math.max(1, Math.min(20, scaleX));
              const constrainedScaleY = 1; // Keep Y scale at 1

              // Calculate chart dimensions
              const chartWidth = width - dynamicMargin.left - dynamicMargin.right;

              // Constrain translateX to keep chart in bounds
              const maxTranslateX = 0;
              const minTranslateX = chartWidth * (1 - constrainedScaleX);
              const constrainedTranslateX = Math.max(minTranslateX, Math.min(maxTranslateX, translateX));

              return {
                ...transformMatrix,
                scaleX: constrainedScaleX,
                scaleY: constrainedScaleY,
                translateX: constrainedTranslateX,
                translateY: 0,
              };
            }}
          >
            {(zoom) => {
              // Store zoom instance
              zoomRef.current = zoom;

              // Update zoom domain when transform changes (but only if it actually changed)
              if (zoom.transformMatrix) {
                const { scaleX, translateX } = zoom.transformMatrix;
                const transformKey = `${scaleX.toFixed(4)}-${translateX.toFixed(2)}`;

                if (transformKey !== lastTransformRef.current) {
                  lastTransformRef.current = transformKey;

                  // Use setTimeout to avoid updating state during render
                  setTimeout(() => {
                    handleZoomTransform(zoom.transformMatrix);
                  }, 0);
                }
              }

              return (
                <svg
                  width={width}
                  height={height}
                  className="overflow-visible"
                  ref={(node) => {
                    // Set both our ref and visx's containerRef
                    svgRef.current = node;
                    if (zoom.containerRef.current !== node) {
                      // @ts-expect-error - visx containerRef is mutable but types don't reflect this
                      zoom.containerRef.current = node;
                    }
                  }}
                  role="img"
                  aria-label="Data chart"
                  style={{
                    cursor: zoom.isDragging ? "grabbing" : "grab",
                    touchAction: "none",
                  }}
                  onMouseDown={zoom.dragStart}
                  onMouseMove={zoom.dragMove}
                  onMouseUp={zoom.dragEnd}
                  onMouseLeave={() => {
                    if (zoom.isDragging) zoom.dragEnd();
                  }}
                  onTouchStart={zoom.dragStart}
                  onTouchMove={zoom.dragMove}
                  onTouchEnd={zoom.dragEnd}
                >
                  {chartContent}
                </svg>
              );
            }}
          </Zoom>
        ) : (
          <svg
            width={width}
            height={height}
            className="overflow-visible"
            ref={svgRef}
            role="img"
            aria-label="Data chart"
          >
            {chartContent}
          </svg>
        )}

        <ChartTooltip data={tooltipData} formatter={yFormatter} />

        {enableBrush && fullDomain && (
          <ChartBrush
            data={datasets}
            seriesMetadata={seriesMetadata}
            width={width}
            height={brushHeight}
            onBrushChange={handleBrushChange}
            brushRegion={zoomDomain}
            margin={dynamicMargin}
          />
        )}

        <ChartBottomBar
          onExport={handleExport}
          items={uniqueSeries}
          marginLeft={0}
          marginRight={dynamicMargin.right}
          isZoomed={zoomDomain !== null}
          onResetZoom={handleResetZoom}
        />
      </ChartContext.Provider>
    </div>
  );
};

/**
 * ChartContainer
 *
 * Main chart wrapper component that provides context and layout for chart visualizations.
 * Handles scaling, tooltips, axes, and data management for area, line, and bar charts.
 */
// Type assertion to fix React 19 compatibility issue with withParentSize HOC
export const ChartContainer = withParentSize(VisXChartContainer) as unknown as React.FC<
  Omit<ChartContainerProps, "parentWidth" | "parentHeight">
>;
